import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, SHADOWS, FONTS } from '../../styles/tokens';
import {
  fetchUsers, fetchLoginEvents,
  type KeycloakUser, type KeycloakEvent,
} from '../../api/keycloak-admin';

/** Security dashboard — sessions, auth stats, failed logins */
export function SecurityDashboard(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [loginEvents, setLoginEvents] = useState<KeycloakEvent[]>([]);
  const [failedLogins, setFailedLogins] = useState<KeycloakEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [allUsers, logins, failures] = await Promise.all([
        fetchUsers(token),
        fetchLoginEvents(token, { type: 'LOGIN', max: 20 }),
        fetchLoginEvents(token, { type: 'LOGIN_ERROR', max: 20 }),
      ]);
      setUsers(allUsers);
      setLoginEvents(logins);
      setFailedLogins(failures);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const activeUsers = users.filter(u => u.enabled).length;
  const disabledUsers = users.filter(u => !u.enabled).length;

  function formatTime(timestamp: number): string {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }

  // Force logout is available via logoutUser(userId) from keycloak-admin API

  const cardStyle = {
    backgroundColor: colors.card,
    borderRadius: '12px',
    boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
    padding: '20px',
  };

  return (
    <div>
      <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: '0 0 20px' }}>
        Security Dashboard
      </h2>

      {error && (
        <div style={{ padding: '12px', backgroundColor: isDark ? '#3b1c1c' : '#fde8e8', border: '1px solid #EF4444', borderRadius: '8px', color: '#EF4444', fontFamily: FONTS.body, fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading security data...</p>
      ) : (
        <>
          {/* Stats cards */}
          <div className="vx-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Total Users', value: users.length, color: COLORS.accent.blue },
              { label: 'Active Users', value: activeUsers, color: '#4ADE80' },
              { label: 'Disabled Users', value: disabledUsers, color: '#EF4444' },
              { label: 'Failed Logins', value: failedLogins.length, color: '#FBBF24' },
            ].map(stat => (
              <div key={stat.label} style={cardStyle}>
                <p style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
                  {stat.label}
                </p>
                <p style={{ fontFamily: FONTS.heading, fontSize: '28px', color: stat.color, margin: '8px 0 0', letterSpacing: '0.05em' }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          {/* Recent logins + Failed logins side by side */}
          <div className="vx-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Recent logins */}
            <div style={cardStyle}>
              <h3 style={{ fontFamily: FONTS.heading, fontSize: '13px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: '0 0 12px' }}>
                Recent Logins
              </h3>
              {loginEvents.length === 0 ? (
                <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>No login events recorded</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {loginEvents.slice(0, 10).map((event, i) => (
                    <div key={i} className="vx-event-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${colors.border}` }}>
                      <div>
                        <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.text }}>
                          {event.details?.username ?? event.userId?.slice(0, 8)}
                        </span>
                        <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginLeft: '8px' }}>
                          {event.ipAddress}
                        </span>
                      </div>
                      <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
                        {formatTime(event.time)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Failed logins */}
            <div style={cardStyle}>
              <h3 style={{ fontFamily: FONTS.heading, fontSize: '13px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: '0 0 12px' }}>
                Failed Login Attempts
              </h3>
              {failedLogins.length === 0 ? (
                <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>No failed login attempts</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {failedLogins.slice(0, 10).map((event, i) => (
                    <div key={i} className="vx-event-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${colors.border}` }}>
                      <div>
                        <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: '#EF4444' }}>
                          {event.details?.username ?? 'Unknown'}
                        </span>
                        <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginLeft: '8px' }}>
                          {event.ipAddress}
                        </span>
                      </div>
                      <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
                        {formatTime(event.time)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
